import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import InfoBox from '../../../../../components/app/alertBox/infoBox'
import FormBuilder from '../../../../../components/app/formBuilder'
import CustomDrawer from '../../../../../components/common/drawer'
import { useCreateOrgUser, useUpdateOrgUser } from '../../../api'
import { getFileName } from '../../../common/commonUtils'
import { ACCEPTED_IMAGE_TYPES, formSchema, OrgUserSchema } from './schema'
import CustomeSideViewer from '../../../../../components/common/drawer/customeSideViewer'
import { humanizeDatetime } from '../../../../../utilities/format'
import moment from 'moment'
type Props = {
  isDrawerOpen: boolean
  disabled?: boolean
  handleClose: () => void
  handleRefresh?: () => void
  paramsId?: any
  handleCallback?: () => void
  model_name?: string
  rowData?: any
  isOwnTask?: boolean
  isGeneral?: boolean
  viewMode?: boolean
  setViewMode?: (value: boolean) => void
  edit?: boolean
  hasPermission?: boolean
  setEdit?: (value: boolean) => void
  subSection?: boolean
  domainType?: string
  setEditViewIndicator?: (value: boolean) => void
  editViewIndicator?: boolean
}

export default function CreateOrganisationUser({
  isDrawerOpen,
  handleClose,
  handleRefresh,
  edit,
  viewMode,
  setViewMode,
  setEdit,
  rowData,
  domainType,
  setEditViewIndicator,
  paramsId,
}: Props) {
  const textField = (
    name: string,
    label: string,
    placeholder: string,
    required = false,
    disabled = false
  ) => ({
    name,
    label,
    id: name,
    type: 'text',
    placeholder,
    ...(required ? { required: true } : {}),
    ...(disabled ? { disabled: true } : {}),
  })
  const statusData = [
    {
      id: '1',
      name: 'Active',
    },
    {
      id: '2',
      name: 'Inactive',
    },
  ]

  const params = useParams()
  const formBuilderProps = [
    {
      ...textField('first_name', 'First Name', 'Enter First Name', true),
      value: rowData?.user?.first_name,
      hidden: false,
    },
    {
      ...textField('last_name', 'Last Name', 'Enter Last Name', true),
      value: rowData?.user?.last_name,
      hidden: false,
    },

    {
      ...textField('job_title', 'Job Title', 'Enter Job Title', false),
      value: rowData?.job_title,
      hidden: false,
    },
    {
      ...textField(
        'contact_number',
        'Contact Number',
        'Enter Contact Number',
        true
      ),
      value: rowData?.contact_number,
      hidden: false,
    },
    {
      ...textField('email', 'Email', 'Enter Email', true),
      type: 'email',
      value: rowData?.user?.username,
      hidden: false,
      disabled: edit,
      toLowercase: true,
    },
    {
      ...textField('password', 'Password', 'Enter Password', false),
      type: 'password',
      hidden: viewMode || edit ? true : false,
    },
    {
      name: 'profile_image',
      required: false,
      label: 'Profile Image',
      id: 'attachment',
      selectedFiles: rowData?.profile_image
        ? {
            name: getFileName(rowData?.profile_image_name),
            link: rowData?.profile_image,
          }
        : '',
      descId: 'id',
      // handleDeleteFile: handleDeleteFile,
      supportedExtensions: ACCEPTED_IMAGE_TYPES,
      supportedFiles: ACCEPTED_IMAGE_TYPES,
      acceptedFiles: 'JPEG, JPG, PNG ',
      type: 'file_upload',
      value: (
        <>
          {rowData?.profile_image ? (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {getFileName(rowData?.profile_image_name) ?? 'File'}
            </span>
          ) : (
            <>--</>
          )}
        </>
      ),
      needConfirmation: false,
    },
    {
      name: 'status',
      label: 'Status',
      id: 'status_id',
      desc: 'name',
      descId: 'id',
      data: statusData,
      type: 'custom_select',
      placeholder: 'Select Status',
      async: false,
      initialLoad: true,
      value: rowData?.user?.status,
      hidden: false,
      required: true,
    },
    {
      ...textField('last_login', 'Last Login', 'Enter Last Login', false),
      // value: rowData?.user?.last_login
      //   ? moment(rowData?.user?.last_login_days_ago).format('DD-MM-YYYY')
      //   : '- -',
      value: rowData?.user?.last_login_days_ago ?? '- -',
      hidden: !viewMode,
    },
    {
      ...textField('datetime_created', 'Created At', 'Enter Created At', false),
      // value: rowData?.user?.datetime_created
      //   ? moment(rowData?.user?.datetime_created).format('DD-MM-YYYY')
      //   : '- -',
      value: rowData?.user?.datetime_created ?? '- -',
      hidden: !viewMode,
    },
    {
      ...textField('datetime_updated', 'Updated At', 'Enter Updated At', false),
      // value: rowData?.user?.datetime_updated
      //   ? moment(rowData?.user?.datetime_updated).format('DD-MM-YYYY')
      //   : '- -',
      value: rowData?.user?.datetime_updated ?? '- -',
      hidden: !viewMode,
    },
  ]

  const handleClearAndClose = () => {
    methods.reset({
      first_name: '',
      last_name: '',
      job_title: '',
      email: '',
      status: '',
      status_id: '',
      password: '',
      profile_image: null,
      contact_number: '',
    })
    handleClose()
  }

  const handleSubmission = () => {
    methods.reset({
      first_name: '',
      last_name: '',
      job_title: '',
      email: '',
      status: '',
      status_id: '',
      password: '',
      profile_image: null,
      contact_number: '',
    })

    handleRefresh?.()
    handleClearAndClose()
  }
  useEffect(() => {
    if (isDrawerOpen) {
      if (!viewMode && edit) {
        methods.reset({
          email: rowData?.user?.username ?? '',
          first_name: rowData?.user?.first_name ?? '',
          last_name: rowData?.user?.last_name ?? '',
          job_title: rowData?.job_title ?? '',
          contact_number: rowData?.contact_number ?? '',
          profile_image: rowData?.profile_image,
          status: rowData?.user?.status ?? '',
        })
      }
    }
  }, [viewMode, edit])
  const onSuccess = () => {
    handleSubmission()
  }
  const { mutate, isLoading: isCreating } = useCreateOrgUser(onSuccess)
  const { mutate: updateMutation, isLoading: isUpdating } =
    useUpdateOrgUser(onSuccess)

  const methods = useForm<OrgUserSchema>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { handleSubmit } = methods
  const onSubmit = (details: any) => {
    const formData = new FormData()
    formData.append('first_name', details?.first_name)
    formData.append('last_name', details?.last_name ?? '')
    formData.append('job_title', details?.job_title ?? '')
    formData.append('contact_number', details?.contact_number ?? '')
    formData.append('username', details?.email ?? '')
    formData.append('password', details?.password ?? '')
    formData.append('profile_image', details?.profile_image ?? '')
    formData.append('status', details?.status ?? '')

    if (rowData?.user?.id) {
      updateMutation(
        {
          id: rowData?.user?.id,
          data: formData,
          orgId: paramsId ? paramsId : params?.id,
        },
        {
          onSuccess: () => {
            handleSubmission()
          },
        }
      )
    } else {
      mutate(
        {
          orgId: params?.id,
          data: formData,
        },
        {
          onSuccess: () => {
            handleSubmission()
          },
        }
      )
    }
  }
  const handleChangeMode = () => {
    setViewMode?.(false)
    setEdit?.(true)
    setEditViewIndicator?.(true)
  }

  const viewHeaderData = {
    image: rowData?.profile_image,
    title: `${rowData?.user?.first_name} ${rowData?.user?.last_name} `,
    subTitle: rowData?.user?.job_title,
    status: rowData?.user?.status,
  }

  const viewContentData = [
    {
      title: 'Communications',
      divide: true,
      value: [
        {
          label: 'email',
          icon: 'email',
          value: rowData?.user?.username,
        },
        {
          label: 'phone',
          icon: 'phone',
          value: rowData?.contact_number,
        },
      ],
    },
    {
      title: 'Last Login',
      value: humanizeDatetime(rowData?.user?.last_login),
    },
    {
      title: 'Created At',
      value: rowData?.user?.datetime_created
        ? moment(new Date(rowData?.user?.datetime_created)).format(
            'DD-MM-YYYY h:mm a'
          )
        : '- -',
    },
    {
      title: 'Updated At',
      value: rowData?.user?.datetime_updated
        ? moment(new Date(rowData?.user?.datetime_updated)).format(
            'DD-MM-YYYY h:mm a'
          )
        : '- -',
    },
  ]

  return (
    <CustomDrawer
      viewMode={viewMode}
      className="formDrawer"
      open={isDrawerOpen}
      actionLabel={viewMode ? 'Edit' : 'Save'}
      handleClose={() => handleClearAndClose()}
      hideSubmit={domainType === 'Assessor' ? true : false}
      actionLoader={isCreating || isUpdating}
      handleSubmit={
        viewMode ? handleChangeMode : handleSubmit((data) => onSubmit(data))
      }
      title={
        edit
          ? 'Edit Organisation User'
          : viewMode
            ? 'Organisation User'
            : 'Create Organisation User'
      }
    >
      <div className="flex flex-col gap-4">
        {!viewMode ? (
          <>
            <FormProvider {...methods}>
              <FormBuilder data={formBuilderProps} edit={true} />
            </FormProvider>
            <InfoBox
              content={`Create a new organisation user by filling out the form and send them an invitation to set their password for accessing the organisation's details within the Organisation Portal.`}
            />
          </>
        ) : (
          <CustomeSideViewer
            headerData={viewHeaderData}
            contentData={viewContentData}
          />
        )}
      </div>
    </CustomDrawer>
  )
}
